import { ICommentaryLayoutRow } from '@common/interfaces/commentary'
import { IAnalysisExhibitSettings, IAnalysisSavedView } from '@common/interfaces/generalAnalysis'
import { IApi } from '../../api'
import { createAction } from '../types'

export const prefix = 'generalAnalysis'

export const LIST_REQUEST = `${prefix}/LIST_REQUEST`
export const LIST_SUCCESS = `${prefix}/LIST_SUCCESS`
export const LIST_FAILURE = `${prefix}/LIST_FAILURE`

export const list = createAction((params: object = {}) => ({
  type: [LIST_REQUEST, LIST_SUCCESS, LIST_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.list(params),
  params,
}))

export const SHOW_REQUEST = `${prefix}/SHOW_REQUEST`
export const SHOW_SUCCESS = `${prefix}/SHOW_SUCCESS`
export const SHOW_FAILURE = `${prefix}/SHOW_FAILURE`

export const show = createAction((id: string) => ({
  type: [SHOW_REQUEST, SHOW_SUCCESS, SHOW_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.show(id),
}))

export const CREATE_REQUEST = `${prefix}/CREATE_REQUEST`
export const CREATE_SUCCESS = `${prefix}/CREATE_SUCCESS`
export const CREATE_FAILURE = `${prefix}/CREATE_FAILURE`

export const create = createAction((data: object) => ({
  type: [CREATE_REQUEST, CREATE_SUCCESS, CREATE_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.create(data),
}))

// Find-or-create the prospect write-up. Reuses the SHOW_* types so the result
// lands in the shared `current` slot (the write-up IS a generalAnalysis), which
// means the existing updateStatus/current merge behavior works for it too.
export const prospectWriteup = createAction((data: object) => ({
  type: [SHOW_REQUEST, SHOW_SUCCESS, SHOW_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.prospectWriteup(data),
}))

// Find-or-create the client's Loan Committee Report. Reuses the SHOW_* types
// for the same reason as prospectWriteup above: the report IS a
// generalAnalysis, so it belongs in the shared `current` slot and inherits the
// status/assignee/saved-view merge behavior already wired there.
export const lcrReport = createAction((data: object) => ({
  type: [SHOW_REQUEST, SHOW_SUCCESS, SHOW_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.lcrReport(data),
}))

export const UPDATE_STATUS_REQUEST = `${prefix}/UPDATE_STATUS_REQUEST`
export const UPDATE_STATUS_SUCCESS = `${prefix}/UPDATE_STATUS_SUCCESS`
export const UPDATE_STATUS_FAILURE = `${prefix}/UPDATE_STATUS_FAILURE`

// Approve the deal in one action (write-up + term structure + committee task).
// Shares the UPDATE_STATUS_* types so `current` merges the new status exactly as
// it does for a status-dropdown approval.
export const approveDeal = createAction((id: string, data: object) => ({
  type: [UPDATE_STATUS_REQUEST, UPDATE_STATUS_SUCCESS, UPDATE_STATUS_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.approveDeal(id, data),
}))

export const updateStatus = createAction((id: string, data: object) => ({
  type: [UPDATE_STATUS_REQUEST, UPDATE_STATUS_SUCCESS, UPDATE_STATUS_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.updateStatus(id, data),
}))

// Sets the effective date on its own. Shares the UPDATE_STATUS_* types so
// `current.amendmentDetails` deep-merges the new date exactly as a status
// change carrying one does, and the event log refreshes the same way.
export const updateEffectiveDate = createAction((id: string, data: object) => ({
  type: [UPDATE_STATUS_REQUEST, UPDATE_STATUS_SUCCESS, UPDATE_STATUS_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.updateEffectiveDate(id, data),
}))

export const CHANGE_TYPE_REQUEST = `${prefix}/CHANGE_TYPE_REQUEST`
export const CHANGE_TYPE_SUCCESS = `${prefix}/CHANGE_TYPE_SUCCESS`
export const CHANGE_TYPE_FAILURE = `${prefix}/CHANGE_TYPE_FAILURE`

// Callers refetch the analysis (show) after this resolves instead of merging
// the response into `current` — the subject relations changed wholesale, so a
// merge would keep the stale old-type subject entity around.
export const changeType = createAction((id: string, data: object) => ({
  type: [CHANGE_TYPE_REQUEST, CHANGE_TYPE_SUCCESS, CHANGE_TYPE_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.changeType(id, data),
}))

export const UPDATE_ASSIGNEE_REQUEST = `${prefix}/UPDATE_ASSIGNEE_REQUEST`
export const UPDATE_ASSIGNEE_SUCCESS = `${prefix}/UPDATE_ASSIGNEE_SUCCESS`
export const UPDATE_ASSIGNEE_FAILURE = `${prefix}/UPDATE_ASSIGNEE_FAILURE`

export const updateAssignee = createAction((id: string, data: object) => ({
  type: [UPDATE_ASSIGNEE_REQUEST, UPDATE_ASSIGNEE_SUCCESS, UPDATE_ASSIGNEE_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.updateAssignee(id, data),
}))

export const UPDATE_SECTION_REQUEST = `${prefix}/UPDATE_SECTION_REQUEST`
export const UPDATE_SECTION_SUCCESS = `${prefix}/UPDATE_SECTION_SUCCESS`
export const UPDATE_SECTION_FAILURE = `${prefix}/UPDATE_SECTION_FAILURE`

export const updateSection = createAction((sectionId: string, data: object) => ({
  type: [UPDATE_SECTION_REQUEST, UPDATE_SECTION_SUCCESS, UPDATE_SECTION_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.updateSection(sectionId, data),
}))

export const ENSURE_EXHIBIT_THREAD_REQUEST = `${prefix}/ENSURE_EXHIBIT_THREAD_REQUEST`
export const ENSURE_EXHIBIT_THREAD_SUCCESS = `${prefix}/ENSURE_EXHIBIT_THREAD_SUCCESS`
export const ENSURE_EXHIBIT_THREAD_FAILURE = `${prefix}/ENSURE_EXHIBIT_THREAD_FAILURE`

/**
 * Opens the comment thread an exhibit hangs its replies from, creating it on
 * the first comment. Resolves with the anchor commentary so the caller can
 * reply on its note straight away.
 */
export const ensureExhibitThread = createAction(
  (analysisId: string, data: { exhibit: string; sectionKey: string }) => ({
    type: [
      ENSURE_EXHIBIT_THREAD_REQUEST,
      ENSURE_EXHIBIT_THREAD_SUCCESS,
      ENSURE_EXHIBIT_THREAD_FAILURE,
    ],
    promise: (api: IApi) => api.generalAnalysis.ensureExhibitThread(analysisId, data),
  }),
)

export const UPDATE_EXHIBIT_REQUEST = `${prefix}/UPDATE_EXHIBIT_REQUEST`
export const UPDATE_EXHIBIT_SUCCESS = `${prefix}/UPDATE_EXHIBIT_SUCCESS`
export const UPDATE_EXHIBIT_FAILURE = `${prefix}/UPDATE_EXHIBIT_FAILURE`

export const updateExhibit = createAction(
  (
    analysisId: string,
    exhibitKey: string,
    // Either half of the exhibit row can be written on its own — the service
    // merges settings rather than replacing them.
    data: { isNotApplicable?: boolean; settings?: IAnalysisExhibitSettings },
  ) => ({
    type: [UPDATE_EXHIBIT_REQUEST, UPDATE_EXHIBIT_SUCCESS, UPDATE_EXHIBIT_FAILURE],
    promise: (api: IApi) => api.generalAnalysis.updateExhibit(analysisId, exhibitKey, data),
  }),
)

export const LIST_EVENTS_REQUEST = `${prefix}/LIST_EVENTS_REQUEST`
export const LIST_EVENTS_SUCCESS = `${prefix}/LIST_EVENTS_SUCCESS`
export const LIST_EVENTS_FAILURE = `${prefix}/LIST_EVENTS_FAILURE`

export const listEvents = createAction(
  (analysisId: string, options: { skipLoader?: boolean } = {}) => ({
    type: [LIST_EVENTS_REQUEST, LIST_EVENTS_SUCCESS, LIST_EVENTS_FAILURE],
    promise: (api: IApi) => api.generalAnalysis.listEvents(analysisId),
    // Forwarding params lets a background refetch pass skipLoader so the shared
    // loadingArray isn't touched and no skeleton flashes (feels like a live update).
    params: { skipLoader: options.skipLoader },
  }),
)

export const LIST_BOARD_MEMBERS_REQUEST = `${prefix}/LIST_BOARD_MEMBERS_REQUEST`
export const LIST_BOARD_MEMBERS_SUCCESS = `${prefix}/LIST_BOARD_MEMBERS_SUCCESS`
export const LIST_BOARD_MEMBERS_FAILURE = `${prefix}/LIST_BOARD_MEMBERS_FAILURE`

// Keyed by client id: the Board Members exhibit reads the company's live CRM
// People, so it refetches per client rather than per analysis. `analysisId` is
// forwarded so an EXECUTED analysis renders the roster frozen at execution — a
// committee-approved exhibit must not keep moving after the fact.
export const listBoardMembers = createAction((clientId: string, analysisId?: string) => ({
  type: [LIST_BOARD_MEMBERS_REQUEST, LIST_BOARD_MEMBERS_SUCCESS, LIST_BOARD_MEMBERS_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.listBoardMembers(clientId, analysisId),
}))

export const LIST_COMMENTARY_REQUEST = `${prefix}/LIST_COMMENTARY_REQUEST`
export const LIST_COMMENTARY_SUCCESS = `${prefix}/LIST_COMMENTARY_SUCCESS`
export const LIST_COMMENTARY_FAILURE = `${prefix}/LIST_COMMENTARY_FAILURE`

export const listCommentary = createAction(
  (analysisId: string, options: { skipLoader?: boolean } = {}) => ({
    type: [LIST_COMMENTARY_REQUEST, LIST_COMMENTARY_SUCCESS, LIST_COMMENTARY_FAILURE],
    promise: (api: IApi) => api.generalAnalysis.listCommentary(analysisId),
    // See listEvents — skipLoader keeps the refetch silent (no skeleton).
    params: { skipLoader: options.skipLoader },
  }),
)

export const ADD_COMMENTARY_REQUEST = `${prefix}/ADD_COMMENTARY_REQUEST`
export const ADD_COMMENTARY_SUCCESS = `${prefix}/ADD_COMMENTARY_SUCCESS`
export const ADD_COMMENTARY_FAILURE = `${prefix}/ADD_COMMENTARY_FAILURE`

// `data` is either a JSON object (text-only New Commentary) or FormData
// when the user dropped files in the modal. Superagent's `.send()` handles
// both transparently; the server's POST handler always forwards
// `req.files` and the service treats them as optional.
export const addCommentary = createAction((analysisId: string, data: object | FormData) => ({
  type: [ADD_COMMENTARY_REQUEST, ADD_COMMENTARY_SUCCESS, ADD_COMMENTARY_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.addCommentary(analysisId, data),
}))

export const UPDATE_COMMENTARY_REQUEST = `${prefix}/UPDATE_COMMENTARY_REQUEST`
export const UPDATE_COMMENTARY_SUCCESS = `${prefix}/UPDATE_COMMENTARY_SUCCESS`
export const UPDATE_COMMENTARY_FAILURE = `${prefix}/UPDATE_COMMENTARY_FAILURE`

// `data` is either a JSON object (note auto-save, field/helper edits)
// or a FormData payload when attaching/removing files. Superagent's
// `.send()` handles both transparently — the server's PATCH handler
// always forwards `req.files`, and the service treats them as optional.
export const updateCommentary = createAction(
  (analysisId: string, commentaryId: string, data: object | FormData) => ({
    type: [UPDATE_COMMENTARY_REQUEST, UPDATE_COMMENTARY_SUCCESS, UPDATE_COMMENTARY_FAILURE],
    promise: (api: IApi) => api.generalAnalysis.updateCommentary(analysisId, commentaryId, data),
  }),
)

export const UPLOAD_COMMENTARY_INLINE_IMAGE_REQUEST = `${prefix}/UPLOAD_COMMENTARY_INLINE_IMAGE_REQUEST`
export const UPLOAD_COMMENTARY_INLINE_IMAGE_SUCCESS = `${prefix}/UPLOAD_COMMENTARY_INLINE_IMAGE_SUCCESS`
export const UPLOAD_COMMENTARY_INLINE_IMAGE_FAILURE = `${prefix}/UPLOAD_COMMENTARY_INLINE_IMAGE_FAILURE`

export const uploadCommentaryInlineImage = createAction((analysisId: string, file: File) => ({
  type: [
    UPLOAD_COMMENTARY_INLINE_IMAGE_REQUEST,
    UPLOAD_COMMENTARY_INLINE_IMAGE_SUCCESS,
    UPLOAD_COMMENTARY_INLINE_IMAGE_FAILURE,
  ],
  promise: (api: IApi) => api.generalAnalysis.uploadCommentaryInlineImage(analysisId, file),
}))

export const SAVE_COMMENTARY_LAYOUT_REQUEST = `${prefix}/SAVE_COMMENTARY_LAYOUT_REQUEST`
export const SAVE_COMMENTARY_LAYOUT_SUCCESS = `${prefix}/SAVE_COMMENTARY_LAYOUT_SUCCESS`
export const SAVE_COMMENTARY_LAYOUT_FAILURE = `${prefix}/SAVE_COMMENTARY_LAYOUT_FAILURE`

// Persists the drag-and-drop arrangement of one tab. `params` carries the
// rows so the reducer can apply the layout optimistically on REQUEST — the
// boxes land where they were dropped without waiting for the round-trip.
// Callers should refetch (listCommentary with skipLoader) on failure to
// resync.
export const saveCommentaryLayout = createAction(
  (analysisId: string, tab: string, rows: ICommentaryLayoutRow[]) => ({
    type: [
      SAVE_COMMENTARY_LAYOUT_REQUEST,
      SAVE_COMMENTARY_LAYOUT_SUCCESS,
      SAVE_COMMENTARY_LAYOUT_FAILURE,
    ],
    promise: (api: IApi) => api.generalAnalysis.saveCommentaryLayout(analysisId, { tab, rows }),
    params: { analysisId, tab, rows },
  }),
)

export const DELETE_COMMENTARY_REQUEST = `${prefix}/DELETE_COMMENTARY_REQUEST`
export const DELETE_COMMENTARY_SUCCESS = `${prefix}/DELETE_COMMENTARY_SUCCESS`
export const DELETE_COMMENTARY_FAILURE = `${prefix}/DELETE_COMMENTARY_FAILURE`

export const deleteCommentary = createAction((analysisId: string, commentaryId: string) => ({
  type: [DELETE_COMMENTARY_REQUEST, DELETE_COMMENTARY_SUCCESS, DELETE_COMMENTARY_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.deleteCommentary(analysisId, commentaryId),
}))

export const REGENERATE_EXPORT_REQUEST = `${prefix}/REGENERATE_EXPORT_REQUEST`
export const REGENERATE_EXPORT_SUCCESS = `${prefix}/REGENERATE_EXPORT_SUCCESS`
export const REGENERATE_EXPORT_FAILURE = `${prefix}/REGENERATE_EXPORT_FAILURE`

export const regenerateExport = createAction((analysisId: string) => ({
  type: [REGENERATE_EXPORT_REQUEST, REGENERATE_EXPORT_SUCCESS, REGENERATE_EXPORT_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.regenerateExport(analysisId),
}))

export const UPLOAD_SIGNED_DOCUMENTS_REQUEST = `${prefix}/UPLOAD_SIGNED_DOCUMENTS_REQUEST`
export const UPLOAD_SIGNED_DOCUMENTS_SUCCESS = `${prefix}/UPLOAD_SIGNED_DOCUMENTS_SUCCESS`
export const UPLOAD_SIGNED_DOCUMENTS_FAILURE = `${prefix}/UPLOAD_SIGNED_DOCUMENTS_FAILURE`

export const uploadSignedDocuments = createAction((analysisId: string, data: FormData) => ({
  type: [
    UPLOAD_SIGNED_DOCUMENTS_REQUEST,
    UPLOAD_SIGNED_DOCUMENTS_SUCCESS,
    UPLOAD_SIGNED_DOCUMENTS_FAILURE,
  ],
  promise: (api: IApi) => api.generalAnalysis.uploadSignedDocuments(analysisId, data),
}))

export const GET_BOX_FOLDER_REQUEST = `${prefix}/GET_BOX_FOLDER_REQUEST`
export const GET_BOX_FOLDER_SUCCESS = `${prefix}/GET_BOX_FOLDER_SUCCESS`
export const GET_BOX_FOLDER_FAILURE = `${prefix}/GET_BOX_FOLDER_FAILURE`

export const getBoxFolder = createAction((analysisId: string) => ({
  type: [GET_BOX_FOLDER_REQUEST, GET_BOX_FOLDER_SUCCESS, GET_BOX_FOLDER_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.getBoxFolder(analysisId),
}))

export const SAVE_VIEW_REQUEST = `${prefix}/SAVE_VIEW_REQUEST`
export const SAVE_VIEW_SUCCESS = `${prefix}/SAVE_VIEW_SUCCESS`
export const SAVE_VIEW_FAILURE = `${prefix}/SAVE_VIEW_FAILURE`

export const saveView = createAction((id: string, savedView: IAnalysisSavedView) => ({
  type: [SAVE_VIEW_REQUEST, SAVE_VIEW_SUCCESS, SAVE_VIEW_FAILURE],
  promise: (api: IApi) => api.generalAnalysis.saveView(id, { savedView }),
}))
